<!-- eslint-disable vue/require-v-for-key -->
<script>
import Multiselect from 'vue-multiselect'
import { Helper, debounce } from '@/mixin/helper'
import { required } from 'vuelidate/lib/validators'
import { getSmsClient } from '@/services/sms'
import RestEditableTable from '@/components/RestEditableTable'

const smsAxios = getSmsClient()

const createDefaultForm = () => ({
  device: null,
  command: null,
  pin: null,
  sensor: null,
})

/**
 * Starter component
 */
export default {
  props: {
    pinCommands: {
      type: Array,
      default: () => [],
    },
    deviceTypes: {
      type: Array,
      default: () => [],
    },
    pinCodes: {
      type: Array,
      default: () => [],
    },
    sensors: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: () => false,
    }
  },
  components: {
    Multiselect,
    RestEditableTable,
  },
  mixins: [Helper],
  emits: ['update-change'],
  data() {
    return {
      selected: [],
      selectedModel: [],
      allSelected: [],
      indeterminate: null,
      filter: null,
      sortDesc: false,
      form: createDefaultForm(),
      isUpdateMode: false,
      selectedPinCommand: null,
      isSubmittingCommand: false,
      showModal: false,
      filteredItems: [],
      selectedDevice: '',
      debounceTimer: null,
      isTableDataAvailable: null,
    }
  },
  validations: {
    form: {
      device: {
        device_code: { required },
      },
      pin: {
        pin_code: { required },
      },
      sensor: {
        sensor_code: { required },
      },
      command: { required },
    },
  },
  watch: {
    'selectedDevice.deviceCode'(newVal, oldVal) {
      if (!newVal && !oldVal) return
      let self = this
      clearTimeout(self.debounceTimer)
      self.debounceTimer = setTimeout(() => {
        self.$emit('update-change', newVal)
      }, 500)
    },
    pinCommands: {
      immediate: true,
      handler(newVal, oldVal) {
        if (!newVal || !oldVal) return
        this.$nextTick()
        this.$refs['pinCommandTableRef'].items = this.initData()
        this.$refs['pinCommandTableRef'].ids = this.pinCommands?.map((e) => e.id)
      },
    },
  },
  computed: {
    /**
     * Total no. of records
     */

    title() {
      return 'Pengaturan Online Command'
    },

    fields() {
      return [
        {
          key: 'device_name',
          label: 'Nama Perangkat',
          sortable: true,
          type: 'text',
        },
        {
          key: 'device_code',
          label: 'Kode Perangkat',
          sortable: true,
          type: 'number',
        },
        {
          key: 'sensor_name',
          label: 'Nama Sensor',
          sortable: true,
          type: 'text',
        },
        {
          key: 'sensor_code',
          label: 'Kode Sensor',
          sortable: true,
          type: 'number',
        },
        {
          key: 'pin_name',
          label: 'Nama Pin',
          sortable: true,
          type: 'text',
        },
        {
          key: 'pin_code',
          label: 'Pin Code',
          sortable: true,
          type: 'number',
        },
        {
          key: 'command',
          label: 'Command',
          sortable: true,
          type: 'text',
        },
        {
          key: 'created_at',
          label: 'Dibuat pada',
          sortable: false,
          type: 'text',
        },
      ]
    },

    apiClient() {
      return {
        get: (param) => {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.filter = param.q ?? ''
          let response = {
            data: this.initData() ?? [],
            page: 1,
            size: this.pinCommands?.length,
            total_count: this.pinCommands?.length,
            total_pages: 1,
          }
          return response
        },
        delete: async (data) => {
          const _ids = data.ids ?? data
          const foundData = this.pinCommands.filter(e => _ids.includes(e.id))  
          await this.toDelete(foundData)
          this.$emit('update-change')
        },
      }
    },
  },
  async created() {
    await this.$nextTick()
  },
  methods: {
    initData() {
      if (!this.pinCommands) return
      this.pinCommands.forEach((data) => {
        data.created_at = Helper.methods.momentIsoToDMYHms(data.created_at)
        data.can_delete = true
        data.id = `${data.device_code}-${data.sensor_code}-${data.pin_code}-${data.command}`
      })
      return this.pinCommands
    },
    getDeviceCode(device) {
      return Number(device?.deviceCode ?? device?.device_code)
    },
    getPostPayload() {
      return {
        device_code: this.getDeviceCode(this.form.device),
        sensor_code: Number(this.form.sensor?.sensor_code),
        command: this.form.command.toString(),
        pin_code: Number(this.form.pin?.pin_code),
      }
    },
    findDeviceOption(deviceCode) {
      return this.deviceTypes.find((device) => Number(device.deviceCode ?? device.device_code) === Number(deviceCode))
    },
    findSensorOption(sensorCode) {
      return this.sensors.find((sensor) => Number(sensor.sensor_code) === Number(sensorCode))
    },
    findPinOption(pinCode, sensorCode) {
      return this.pinCodes.find((pin) => {
        const samePin = Number(pin.pin_code) === Number(pinCode)
        const sameSensor = pin.sensor_code === undefined || Number(pin.sensor_code) === Number(sensorCode)
        return samePin && sameSensor
      })
    },
    async addPinCommand() {
      try {
        this.isSubmittingCommand = true
        const res = await smsAxios.post('/pin-command', this.getPostPayload())
        if (!res) return
        this.$toast.success(`Data berhasil tersimpan`)
        this.resetData()
        this.hideModal()
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isSubmittingCommand = false
      }
    },
    async updatePinCommand() {
      try {
        this.isSubmittingCommand = true
        const res = await smsAxios.patch('/pin-command', {
          previous_value: this.selectedPinCommand,
          new_value: this.getPostPayload(),
        })
        if (!res) return
        this.$toast.success(`Data berhasil diubah`)
        this.resetData()
        this.hideModal()
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isSubmittingCommand = false
      }
    },
    submitPinCommand() {
      if (this.isUpdateMode) {
        this.updatePinCommand()
        return
      }
      this.addPinCommand()
    },
    showModalCreate() {
      this.isUpdateMode = false
      this.selectedPinCommand = null
      this.form = createDefaultForm()
      this.$bvModal.show('modal-create')
    },
    showModalUpdate(item, index, event) {
      if (event?.target?.closest?.('button, a, input, label, .form-check')) return
      this.isUpdateMode = true
      this.selectedPinCommand = {
        device_code: Number(item.device_code),
        sensor_code: Number(item.sensor_code),
        command: item.command,
        pin_code: Number(item.pin_code),
      }
      this.form = {
        device: this.findDeviceOption(item.device_code) ?? null,
        sensor: this.findSensorOption(item.sensor_code) ?? null,
        pin: this.findPinOption(item.pin_code, item.sensor_code) ?? null,
        command: item.command,
      }
      this.$bvModal.show('modal-create')
    },

    async toDelete(data) {
      let promises = []
      for (const element of data) {
        const res = await this.deletePinCommand(element)
        if (!res) continue
        promises.push()
      }

      Promise.all(promises).then(() => {
        this.$toast.success(`Data berhasil dihapus`)
        this.resetData()
      })
    },

    async deletePinCommand(data) {
      try {
        const res = await smsAxios.delete('/pin-command', {
          data: {
            device_code: data.device_code,
            command: data.command,
            pin_code: data.pin_code,
            sensor_code: data.sensor_code,
          },
        })
        return res
      } catch (error) {
        this.handleError(error)
      }
    },

    resetModal() {
      this.form = createDefaultForm()
      this.isUpdateMode = false
      this.selectedPinCommand = null
      this.isSubmittingCommand = false
    },
    hideModal() {
      this.$nextTick(() => {
        this.$bvModal.hide('modal-create')
      })
    },
    handleOk(bvModalEvent) {
      // Prevent modal from closing
      bvModalEvent.preventDefault()
      // Trigger submit handler
      this.handleSubmit()
    },
    handleError(error) {
      if (error.response) {
        const errorStatus = error.response.status
        const errorData = error.response.data

        if (errorStatus === 400 && errorData.details) {
          errorData.details.forEach((element) => {
            this.$toast.error(`${errorData.error}: ${element.field} ${element.message}`)
          })
        } else if (errorStatus === 401) {
          this.$toast.error(errorData.error)
          this.$router.push({ name: 'Login' })
        } else if (errorStatus === 403) {
          this.$toast.error(errorData.error)
        } else {
          this.$toast.error(errorData.error)
        }
      } else if (error.request) {
        this.$toast.error(error.response.statusText)
      } else {
        this.$toast.error(error.message)
      }
    },

    handleSubmit() {
      this.submitPinCommand()
    },
    customLabelDevice(item) {
      if (item.deviceName) return `${item.deviceName}`
    },
    searchData: debounce(function () {
      this.selectedDevice = ''
      this.$emit('update-change', '')
    }, 500),
    resetData() {
      let self = this
      clearTimeout(self.debounceTimer)
      self.debounceTimer = setTimeout(() => {
        self.$refs['pinCommandTableRef'].handleUnselectAll()
        self.$emit('update-change', this.selectedDevice?.deviceCode)
      }, 500)
    },
    handleDataAvailable(isAvailable) {
      this.isTableDataAvailable = isAvailable
    },
    async handleChange() {
      const pinCommandTableRef = this.$refs.pinCommandTableRef.$refs
      await this.$nextTick()
      this.fields.forEach((field) => {
        if (!field.editable) return
        pinCommandTableRef[`${field.key}-ref`].$el.addEventListener(
          'keydown',
          Helper.methods.preventTabKey
        )
      })
    },
  },
}
</script>
<style>
.formulate-input ul.formulate-input-errors {
  font-style: normal;
  font-size: 80%;
}
.pin-command-table .b-table tbody tr {
  cursor: pointer;
}
</style>
<template>
  <div>
    <h4 class="card-title">{{ $t('onlineCommand.chooseDevice') }}</h4>
    <div class="align-items-baseline align-self-center d-flex justify-content-between">
      <div class="col-4">
        <b-form-group id="licensePlate_plate" label-class="required">
          <multiselect v-model="selectedDevice" :options="deviceTypes" label="deviceName" />
        </b-form-group>
      </div>
      <div class="text-end mb-3">
        <b-button
          v-if="selected.length > 0"
          @click="toDelete"
          variant="outline-danger"
          class="mx-2"
        >
          <i class="bx bx-trash-alt"></i> {{ $t('global.delete') }}
        </b-button>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <RestEditableTable
              class="pin-command-table"
              ref="pinCommandTableRef"
              searchable
              :fields="fields"
              :apiClient="apiClient"
              :bulkDeleteApi="true"
              :idIsString="true"
              :isEnableEditButton="false"
              @available-change="handleDataAvailable"
              trackBy="id"
              @changeData="handleChange"
              @row-clicked="showModalUpdate"
              :hidePagination="true"
              :addWithModal="showModalCreate"
              :customSearch="filter"
              :showNoData="true"
              :busy="isLoading"
              sortBy="device_code"
              :useThousandFormat="false"
            >
            </RestEditableTable>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      class="mc-modal"
      id="modal-create"
      :title="isUpdateMode ? 'Update Pin Command' : 'Pengaturan Pin Command'"
      size="lg"
      centered
      scrollable
      @hidden="resetModal"
      @ok="handleOk"
    >
      <form ref="form" class="mc-form" @submit.stop.prevent="handleSubmit">
        <b-row>
          <!-- Device Code -->
          <b-col md="12">
            <div class="mb-3 mc-input-info">
              <label class="mt-3 required">{{ $t('setting.form.deviceType') }}</label>
              <multiselect
                ref="deviceTypeRef"
                v-model="form.device"
                :options="deviceTypes"
                label="deviceName"
                placeholder="Tipe Perangkat"
                :allow-empty="false"
                :custom-label="customLabelDevice"
              ></multiselect>
              <div v-if="$v.form.device.device_code.$error" class="invalid-feedback">
                <span v-if="!$v.form.device.device_code.required">
                  {{ $t('setting.form.deviceType') }} {{ $t('global.validation.required') }}
                </span>
              </div>
            </div>
            <!-- End Device Code-->
            <!-- Sensor Code -->
            <div class="mb-3 mc-input-info">
              <b-form-group
                id="sensor_code"
                label="Kode Sensor"
                label-for="sensor_code"
                label-class="required"
              >
                <multiselect
                  ref="deviceTypeRef"
                  v-model="form.sensor"
                  :options="sensors"
                  placeholder="Tipe Sensor"
                  :allow-empty="false"
                  :class="{
                    'is-invalid': $v.form.sensor.sensor_code.$error,
                  }"
                  :custom-label="(e) => `${e.sensor_name} (${e.sensor_code})`"
                ></multiselect>
                <div v-if="$v.form.sensor.sensor_code.$error" class="invalid-feedback">
                  <span v-if="!$v.form.sensor.sensor_code.required">
                    {{ $t('setting.form.sensor_code') }} {{ $t('global.validation.required') }}
                  </span>
                </div>
              </b-form-group>
            </div>
            <!-- End Sensor Code -->
            <!-- Pin Code -->
            <div class="mb-3 mc-input-info">
              <b-form-group
                id="pin_code"
                label="Kode Pin"
                label-for="pin_code"
                label-class="required"
              >
                <multiselect
                  ref="deviceTypeRef"
                  v-model="form.pin"
                  :options="pinCodes"
                  placeholder="Tipe Perangkat"
                  :allow-empty="false"
                  :class="{
                    'is-invalid': $v.form.pin.pin_code.$error,
                  }"
                  :custom-label="(e) => `${e.pin_name} (${e.pin_code})`"
                ></multiselect>
                <div v-if="$v.form.pin.pin_code.$error" class="invalid-feedback">
                  <span v-if="!$v.form.pin.pin_code.required">
                    {{ $t('setting.form.pin_code') }} {{ $t('global.validation.required') }}
                  </span>
                </div>
              </b-form-group>
            </div>
            <!-- End Pin Code -->
            <!-- Command -->
            <div class="mc-input-info">
              <b-form-group id="command" label="Command" label-for="command" label-class="required">
                <b-form-input
                  id="command"
                  placeholder="Online command"
                  v-model="form.command"
                  trim
                  :class="{
                    'is-invalid': $v.form.command.$error,
                  }"
                ></b-form-input>
                <div v-if="$v.form.command.$error" class="invalid-feedback">
                  <span v-if="!$v.form.command.required">
                    {{ $t('setting.form.command') }} {{ $t('global.validation.required') }}
                  </span>
                </div>
              </b-form-group>
            </div>
            <!-- End Command -->
          </b-col>
        </b-row>
      </form>
      <template #modal-footer="{ cancel }">
        <b-row class="mc-modal__filter-footer">
          <b-col sm="12" md="8" class="mc-modal__filter-footer-fav"> </b-col>

          <b-col sm="12" md="4" class="mc-modal__filter-footer-btn">
            <b-button variant="outline-primary" @click="cancel" class="me-3"> Batal </b-button>
            <b-button type="submit" variant="primary" :disabled="isSubmittingCommand" @click="submitPinCommand"> {{ isUpdateMode ? 'Update' : 'Simpan' }} </b-button>
          </b-col>
        </b-row>
      </template>
    </b-modal>
  </div>
</template>
